import React from 'react';
import { Box, Flex, HStack, Text, Tooltip } from '@chakra-ui/react';
import SiblingDiscountBadge from './SiblingDiscountBadge';

const formatEuro = (value) =>
  `€${Number(value || 0).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function PaymentStepBar({ step, onClick, disabledHint }) {
  const { label, due, isDone, canPay } = step;
  const isClickable = canPay && !isDone;

  const borderColor = isDone ? 'green.500' : canPay ? 'orange.400' : 'gray.600';
  const bg = isDone ? 'green.900' : canPay ? 'orange.900' : '#2f3136';

  let statusText;
  let statusColor = 'gray.400';

  if (isDone) {
    statusText = 'Pagato';
    statusColor = 'green.300';
  } else if (canPay && due > 0.01) {
    statusText = `${formatEuro(due)} da pagare`;
    statusColor = 'orange.200';
  } else if (due > 0.01) {
    statusText = formatEuro(due);
    statusColor = 'gray.500';
  } else {
    statusText = '—';
  }

  const content = (
    <Box
      flex={1}
      minW={{ base: '100%', md: '140px' }}
      px={2.5}
      py={2}
      borderRadius="8px"
      border="1px solid"
      borderColor={borderColor}
      bg={bg}
      cursor={isClickable ? 'pointer' : 'default'}
      onClick={isClickable ? onClick : undefined}
      transition="all 0.15s ease"
      _hover={isClickable ? { borderColor: '#ffcc00' } : undefined}
      opacity={!isDone && !canPay && due <= 0 ? 0.7 : 1}
    >
      <Flex justify="space-between" align="center" gap={2}>
        <Text fontSize="xs" fontWeight="700" color="white" whiteSpace="nowrap">
          {label}
        </Text>
        <Text fontSize="xs" color={statusColor} textAlign="right">
          {statusText}
          {isClickable && (
            <Text as="span" color="#ffcc00" fontWeight="600" ml={1}>
              →
            </Text>
          )}
        </Text>
      </Flex>
    </Box>
  );

  if (disabledHint && !isClickable && !isDone) {
    return <Tooltip label={disabledHint}>{content}</Tooltip>;
  }

  return content;
}

const VariantPaymentTimeline = ({ line, onStepPay, hideSocioName = false }) => {
  const steps = line.paymentSteps || [];

  const handleStepClick = (step) => {
    if (step.isDone || !step.canPay) return;
    onStepPay?.(line, step);
  };

  const saldoLockedHint =
    steps.find((s) => s.type === 'acconto')?.isDone === false
      ? "Completa prima l'acconto"
      : '';

  return (
    <Box
      bg="#2f3136"
      borderRadius="8px"
      px={3}
      py={2}
      border="1px solid"
      borderColor={line.bugLine ? 'orange.600' : 'gray.600'}
    >
      <Flex
        justify="space-between"
        align="center"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Box flex={1} minW={0}>
          <Text fontWeight="600" color="white" fontSize="sm" noOfLines={1}>
            {line.variantName}
          </Text>
          <HStack mt={0.5} spacing={2} flexWrap="wrap">
            {!hideSocioName && line.socioName && (
              <Text fontSize="xs" color="gray.400">
                {line.socioName}
              </Text>
            )}
            {line.applyDiscount && <SiblingDiscountBadge size="2xs" />}
          </HStack>
        </Box>
        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
          {formatEuro(line.fullTotal)}
        </Text>
      </Flex>

      <HStack align="stretch" spacing={2} flexWrap="wrap">
        {steps.map((step) => (
          <PaymentStepBar
            key={`${line.variantId}-${step.type}`}
            step={step}
            onClick={() => handleStepClick(step)}
            disabledHint={
              step.type === 'saldo' && !step.canPay ? saldoLockedHint : ''
            }
          />
        ))}
      </HStack>
    </Box>
  );
};

export default VariantPaymentTimeline;
